import { useAtomValue } from "jotai"
import { RefObject, useCallback, useEffect, useId } from "react"
import { beginAtom } from "../state"
import { isPlayButton } from "../components/TogglePlayButton"
import { isInsideToolbar } from "@stringtale/react"

/** Which scoped player currently owns the spacebar shortcut. */
let spacebarOwnerId: string | null = null

type Options = {
  /**
   * When set, spacebar only toggles this player (not every mounted player).
   * The last interacted player owns the shortcut; a lone player claims it on
   * mount so space works without hovering.
   */
  rootRef?: RefObject<HTMLElement | null>
}

export default function usePlaybackToggle(
  elementRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>,
  options?: Options
) {
  const begin = useAtomValue(beginAtom)
  const rootRef = options?.rootRef
  const ownerId = useId()

  const claimSpacebar = useCallback(() => {
    spacebarOwnerId = ownerId
  }, [ownerId])

  const toggle = useCallback(
    (replayOrEvent?: "REPLAY" | any) => {
      if (!elementRef) return
      const video = elementRef.current
      if (!video) return
      if (rootRef) claimSpacebar()
      if (video.paused) {
        if (begin && (!video.currentTime || replayOrEvent === "REPLAY")) {
          video.currentTime = begin
        }
        video.play().catch()
      } else {
        video.pause()
      }
    },
    [begin, elementRef, rootRef, claimSpacebar]
  )

  useEffect(() => {
    if (!rootRef) return

    // Lone / first scoped player: space works immediately, no hover needed.
    if (spacebarOwnerId === null) {
      spacebarOwnerId = ownerId
    }

    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current
      if (!(e.target instanceof Node)) return
      if (root?.contains(e.target)) claimSpacebar()
    }

    document.addEventListener("pointerdown", onPointerDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      if (spacebarOwnerId === ownerId) {
        spacebarOwnerId = null
      }
    }
  }, [rootRef, ownerId, claimSpacebar])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isInsideToolbar(e)) return
      if (e.key !== " ") return
      if (e.target instanceof HTMLInputElement) return
      if (e.target instanceof HTMLTextAreaElement) return
      if (e.target && isPlayButton(e.target)) return

      if (rootRef) {
        // Previous owner unmounted: first scoped handler reclaims.
        if (spacebarOwnerId === null) {
          spacebarOwnerId = ownerId
        }
        if (spacebarOwnerId !== ownerId) return
      }

      e.preventDefault()
      toggle()
    }
    document?.addEventListener("keypress", handler)
    return () => {
      document?.removeEventListener("keypress", handler)
    }
  }, [toggle, rootRef, ownerId])

  return toggle
}
